<template>
  <v-footer app class="derracare-footer">
    <div class="footer-inner">
      <!-- Brand -->
      <div class="footer-brand">
        <span class="footer-title">DerraCare</span>
        <span class="footer-divider" aria-hidden="true">|</span>
        <span class="footer-copyright">© {{ currentYear }} DerraCare Intelligence</span>
      </div>

      <!-- Links -->
      <nav class="footer-links" aria-label="Footer navigation">
        <a
          v-for="link in footerLinks"
          :key="link.title"
          :href="link.href"
          class="footer-link"
          @click.prevent="handleLinkClick(link)"
        >
          {{ link.title }}
        </a>
      </nav>
    </div>
  </v-footer>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useAppStore } from "@/stores/app";

const router = useRouter();
const appStore = useAppStore();

const currentYear = computed(() => new Date().getFullYear());

const footerLinks = [
  {
    title: "Privacy",
    href: "/privacy",
    action: "page",
  },
  {
    title: "Terms",
    href: "/terms",
    action: "page",
  },
  {
    title: "Help",
    href: "/help",
    action: "page",
  },
  {
    title: "Contact",
    href: "mailto:support@askmydoctor.com",
    action: "external",
  },
];

const handleLinkClick = (link: any) => {
  if (link.action === "external") {
    window.open(link.href, "_blank");
  } else if (link.action === "page") {
    appStore.showInfo(`${link.title}`, `${link.title} page will be implemented`);
  }
};
</script>

<style scoped lang="scss">
/* ─── Base Footer ─────────────────────────────────────────── */
.derracare-footer {
  background: #f8fafc !important;
  border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
  color: #64748b;
  font-family: "Outfit", "Inter", sans-serif;
  height: auto !important;
  min-height: 48px !important;
  padding: 0 !important;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* ─── Inner wrapper ───────────────────────────────────────── */
.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 24px;
  min-height: 48px;
  flex-wrap: wrap;
  gap: 4px;
}

/* ─── Brand block ─────────────────────────────────────────── */
.footer-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.footer-title {
  color: #1e293b;
  font-size: 0.875rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

.footer-divider {
  opacity: 0.2;
  font-size: 0.875rem;
  user-select: none;
}

.footer-copyright {
  font-size: 0.72rem;
  font-weight: 500;
  opacity: 0.65;
  white-space: nowrap;
}

/* ─── Navigation Links ────────────────────────────────────── */
.footer-links {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.footer-link {
  color: #64748b;
  text-decoration: none;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 4px 0;
  transition: color 0.25s ease;
  white-space: nowrap;

  /* Touch-friendly tap target */
  min-height: 44px;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: #1193d4 !important;
  }
}

/* ─── Dark Mode ───────────────────────────────────────────── */
:global(html.dark) .derracare-footer,
:global(.dark) .derracare-footer {
  background: #0c0d10 !important;
  border-top-color: rgba(255, 255, 255, 0.08) !important;
  color: #9ca3af;
}

:global(html.dark) .footer-title,
:global(.dark) .footer-title {
  color: #f3f4f6;
}

:global(html.dark) .footer-copyright,
:global(.dark) .footer-copyright {
  color: #9ca3af;
}

:global(html.dark) .footer-link,
:global(.dark) .footer-link {
  color: #9ca3af;

  &:hover {
    color: #11a4d4 !important;
  }
}

/* ─── Tablet: 600–960px ───────────────────────────────────── */
@media (max-width: 960px) {
  .footer-inner {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 16px;

    /* iOS safe area for home-bar */
    padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  }

  .footer-brand {
    justify-content: center;
    width: 100%;
    gap: 6px;
  }

  .footer-copyright {
    font-size: 0.68rem;
  }

  .footer-links {
    justify-content: center;
    width: 100%;
    gap: 12px;
  }

  .footer-link {
    font-size: 0.7rem;
    min-height: 44px; /* keep touch target */
  }
}

/* ─── Mobile: <480px ──────────────────────────────────────── */
@media (max-width: 480px) {
  .footer-inner {
    gap: 8px;
    padding: 12px 12px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  }

  .footer-brand {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 2px;
  }

  /* Visually separate brand from links */
  .footer-links {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    padding-top: 8px;
    width: 100%;
    justify-content: center;
    gap: 16px;
  }

  :global(.dark) .footer-links {
    border-top-color: rgba(255, 255, 255, 0.06);
  }

  .footer-title {
    font-size: 0.8rem;
  }

  .footer-copyright {
    font-size: 0.65rem;
  }

  .footer-link {
    font-size: 0.68rem;
    letter-spacing: 0.015em;
  }
}
</style>
